import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
} from '@mui/material';

interface EntityData {
  count: number;
  firstAt?: string;
  lastAt?: string;
  daysSinceFirst?: number;
  daysSinceLast?: number;
  ttf?: number;
  last7Days?: number;
  last30Days?: number;
  totalAmount?: number;
}

interface EntityDetailsModalProps {
  open: boolean;
  onClose: () => void;
  entityType: string;
  entityData: EntityData | null;
  companyName: string;
}

/**
 * Modal showing detailed entity statistics
 */
export const EntityDetailsModal: React.FC<EntityDetailsModalProps> = ({
  open,
  onClose,
  entityType,
  entityData,
  companyName,
}) => {
  if (!entityData) return null;

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount?: number): string => {
    if (amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const displayName = entityType.charAt(0).toUpperCase() + entityType.slice(1);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight={600}>
          {displayName} Details
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {companyName}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ py: 1 }}>
          {/* Count and TTF in same row */}
          <Box sx={{ display: 'flex', gap: 4, mb: 2 }}>
            {/* Total Count */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" textTransform="uppercase">
                Total Count
              </Typography>
              <Typography variant="h4" fontWeight={600} color="text.primary">
                {entityData.count}
              </Typography>
            </Box>

            {/* TTF - Time To First */}
            {entityData.ttf !== undefined && (
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" textTransform="uppercase">
                  Time To First (TTF)
                </Typography>
                <Typography variant="h4" fontWeight={600} color="text.primary">
                  {entityData.ttf} {entityData.ttf === 1 ? 'day' : 'days'}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  From account creation to first {entityType}
                </Typography>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* First Created */}
          {entityData.firstAt && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>First Created:</strong> {formatDate(entityData.firstAt)}
              </Typography>
              {entityData.daysSinceFirst !== undefined && (
                <Typography variant="caption" color="text.secondary">
                  {entityData.daysSinceFirst} days ago
                </Typography>
              )}
            </Box>
          )}

          {/* Last Created */}
          {entityData.lastAt && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Last Created:</strong> {formatDate(entityData.lastAt)}
              </Typography>
              {entityData.daysSinceLast !== undefined && (
                <Typography variant="caption" color="text.secondary">
                  {entityData.daysSinceLast} {entityData.daysSinceLast === 1 ? 'day' : 'days'} ago
                </Typography>
              )}
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Recent Activity */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Last 7 Days:</strong> {entityData.last7Days ?? 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Last 30 Days:</strong> {entityData.last30Days ?? 'N/A'}
            </Typography>
          </Box>

          {/* Total Amount (if revenue tracking enabled) */}
          {entityData.totalAmount !== undefined && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" textTransform="uppercase">
                  Total Amount
                </Typography>
                <Typography variant="h5" fontWeight={600} color="success.main">
                  {formatCurrency(entityData.totalAmount)}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
